import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { BusinessSector } from "@enums/businessSector.enum";
import { CrmEventAttendingStatus } from "@enums/crmEventAttendingStatus.enum";
import { CrmEventType } from "@enums/crmEventType.enum";
import { CrmInteractionRating } from "@enums/crmInteractionRating.enum";
import { CrmLeadSource } from "@enums/crmLeadSource.enum";
import { CrmLeadStatus } from "@enums/crmLeadStatus.enum";
import { InteractionType } from "@enums/interactionType.enum";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { Client } from "@interfaces/models/client.interface";
import { CrmContact } from "@interfaces/models/crmContact.interface";
import { CrmEvent } from "@interfaces/models/crmEvent.interface";
import { CrmEventAttendee } from "@interfaces/models/crmEventAttendee.interface";
import { CrmInteraction } from "@interfaces/models/crmInteraction.interface";
import { CrmLead } from "@interfaces/models/crmLead.interface";
import { CrmOpportunity } from "@interfaces/models/crmOpportunity.interface";
import { CrmOpportunityStage } from "@enums/crmOpportunityStage.enum";
import { CrmTask } from "@interfaces/models/crmTask.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { AuthService } from "@services/auth.service";
import { ClientService } from "@services/client.service";
import { CrmContactService } from "@services/crmContact.service";
import { CrmEventAttendeeService } from "@services/crmEventAttendee.service";
import { CrmEventService } from "@services/crmEvent.service";
import { CrmInteractionService } from "@services/crmInteraction.service";
import { CrmLeadService } from "@services/crmLead.service";
import { CrmOpportunityService } from "@services/crmOpportunity.service";
import { CrmTaskService } from "@services/crmTask.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "crm",
	styleUrls: ["crm.css"],
	templateUrl: "crm.html"
})
export class CrmDashboardAdminComponent {
	attendingStatuses: any[] = [];
	businessSectors: any[] = [];
	clients: Client[] = [];
	contacts: CrmContact[] = [];
	eventTypes: any[] = [];
	events: CrmEvent[] = [];
	expandedTaskId: number | null = null;
	fabOpen = false;
	filteredPopupContacts: any[] = [];
	followUps: CrmInteraction[] = [];
	headerPrimaryButtons: AdminHeaderButton[] = [];
	interactionTypes: any[] = [];
	isCrmEnhanced = false;
	leadSources: any[] = [];
	leadStatuses: any[] = [];
	leads: CrmLead[] = [];
	myTasks: CrmTask[] = [];
	opportunities: CrmOpportunity[] = [];
	pendingDismissFollowUp: CrmInteraction | null = null;
	popupContact: CrmContact = new CrmContact();
	popupEvent: CrmEvent = new CrmEvent();
	popupInteraction: CrmInteraction = new CrmInteraction();
	popupInteractionFollowUp: string | null = null;
	popupLead: CrmLead = new CrmLead();
	popupSaveLabel = "Save";
	popupTask: CrmTask = new CrmTask();
	popupType = "";
	popupVisible = false;
	quickOutcomes = ["Tyre kicker", "Follow up required", "Golden nugget", "Meeting to be arranged", "Don't contact again", "RFQ Expected", "PO Expected"];
	quickSummaries = ["Chased quote feedback", "Chased for RFQ", "Invited to site", "Requested customer site visit", "General check-in", "Reintroduction", "Sent brochure/presentation", "Sent merchandise", "Chased CAD/Drawings"];
	recentInteractions: CrmInteraction[] = [];
	title = "CRM Dashboard";
	upcomingEvents: CrmEvent[] = [];
	userProfile: UserProfile = new UserProfile();
	userProfiles: UserProfile[] = [];
	viewAsUserProfileId: number | null = null;
	viewEvent: CrmEvent = new CrmEvent();
	viewEventAttendees: CrmEventAttendee[] = [];
	viewEventVisible = false;
	viewInteraction: CrmInteraction = new CrmInteraction();
	viewInteractionVisible = false;

	get contactedLeads() { return this.leads.filter(l => l.status === CrmLeadStatus.Contacted).length; }
	get newLeads() { return this.leads.filter(l => l.status === CrmLeadStatus.New).length; }
	get qualifiedLeads() { return this.leads.filter(l => l.status === CrmLeadStatus.Qualified).length; }
	get wonOpportunities() {
		const twoWeeksAgo = new Date();
		twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
		return this.opportunities.filter(o => o.stage === CrmOpportunityStage.ClosedWon && o.actualCloseDate && new Date(o.actualCloseDate) >= twoWeeksAgo).length;
	}

	constructor(private router: Router, private toastService: ToastService, private appSettingsService: AppSettingsService, private authService: AuthService, private clientService: ClientService, private crmContactService: CrmContactService, private crmEventAttendeeService: CrmEventAttendeeService, private crmEventService: CrmEventService, private crmInteractionService: CrmInteractionService, private crmLeadService: CrmLeadService, private crmOpportunityService: CrmOpportunityService, private crmTaskService: CrmTaskService, private userProfileService: UserProfileService) {
		this.headerPrimaryButtons = [{ icon: "fal fa-columns", method: "openPipeline", text: "Pipeline View" }];
		this.attendingStatuses = GetPropertiesFromEnum(CrmEventAttendingStatus);
		this.eventTypes = GetPropertiesFromEnum(CrmEventType);
		this.interactionTypes = GetPropertiesFromEnum(InteractionType);
		this.businessSectors = GetPropertiesFromEnum(BusinessSector);
		this.leadSources = GetPropertiesFromEnum(CrmLeadSource);
		this.leadStatuses = GetPropertiesFromEnum(CrmLeadStatus);
		this.isCrmEnhanced = CheckGroupPermissions(this.appSettingsService, "CRM Enhanced") ?? false;
		this.getClients();
		this.getContacts();
		this.getEvents();
		this.getFollowUps();
		this.getLeads();
		this.getMyTasks();
		this.getOpportunities();
		this.getRecentInteractions();
		this.getUpcomingEvents();
		this.getUserProfile();
		this.getUserProfiles();
	}

	addQuickOutcome(text: string) {
		this.popupInteraction.outcome = this.popupInteraction.outcome ? this.popupInteraction.outcome + "\n" + text : text;
	}

	addQuickSummary(text: string) {
		this.popupInteraction.summary = this.popupInteraction.summary ? this.popupInteraction.summary + "\n" + text : text;
	}

	closePopup() { this.popupVisible = false; this.pendingDismissFollowUp = null; }

	dismissFollowUp(interaction: CrmInteraction) {
		const updated = { ...interaction, followUpDate: null };
		this.crmInteractionService.updateSingleById(interaction.id, updated).subscribe(
			() => { this.getFollowUps(); },
			(err) => { console.log(err); }
		);
	}

	fabNewOpportunity() { this.router.navigate(["admin/crmOpportunities/create"]); }

	fabNewTask() { this.openTaskModal(); }

	getAttendeeName(userProfileId: number): string {
		return this.userProfiles.find(u => u.id === userProfileId)?.fullName ?? "Unknown";
	}

	getClients() {
		this.clientService.getAll().subscribe(
			(res: any) => { this.clients = res.response.items; },
			(err) => { console.log(err); }
		);
	}

	getContacts() {
		this.crmContactService.getAll().subscribe(
			(res: any) => {
				this.contacts = (res.response.items || []).map((c: any) => ({ ...c, fullName: ((c.firstName || "") + " " + (c.lastName || "")).trim() }));
			},
			(err) => { console.log(err); }
		);
	}

	getEventAttendees(eventId: number) {
		this.crmEventAttendeeService.getByEventId(eventId).subscribe(
			(res: any) => { this.viewEventAttendees = res.response || []; },
			(err) => { console.log(err); }
		);
	}

	getEvents() {
		this.crmEventService.getAll().subscribe(
			(res: any) => { this.events = res.response.items; },
			(err) => { console.log(err); }
		);
	}

	getFollowUpDaysLabel(followUpDate: Date | undefined): string {
		if (!followUpDate) return "—";
		const today = new Date(); today.setHours(0, 0, 0, 0);
		const due = new Date(followUpDate); due.setHours(0, 0, 0, 0);
		const days = Math.round((due.getTime() - today.getTime()) / 86400000);
		if (days === 0) return "Today";
		if (days === 1) return "Tomorrow";
		if (days === -1) return "1 day overdue";
		if (days < 0) return `${-days} days overdue`;
		return `${days} days`;
	}

	getFollowUps() {
		this.crmInteractionService.getUpcomingFollowUps().subscribe(
			(res: any) => { this.followUps = res.response || []; },
			(err) => { console.log(err); }
		);
	}

	getInteractionLink(interaction: CrmInteraction): string {
		if (interaction.clientId) { return this.clients.find(c => c.id === interaction.clientId)?.name ?? "Client"; }
		if (interaction.crmLeadId) { return this.leads.find(l => l.id === interaction.crmLeadId)?.companyName ?? "Lead"; }
		return "—";
	}

	getInteractionTypeName(interaction: CrmInteraction): string {
		return this.interactionTypes.find(t => t.id === interaction.interactionType)?.name ?? "—";
	}

	getLeads() {
		this.crmLeadService.getAll().subscribe(
			(res: any) => { this.leads = (res.response.items || []).filter((l: any) => l.isActive); },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	getMyTasks() {
		let req;
		if (this.viewAsUserProfileId) {
			req = this.crmTaskService.getTasksForUser(this.viewAsUserProfileId);
		} else if (this.isCrmEnhanced) {
			req = this.crmTaskService.getAll();
		} else {
			req = this.crmTaskService.getMyTasks();
		}
		req.subscribe(
			(res: any) => {
				const tasks = res.response?.items || res.response || [];
				this.myTasks = this.isCrmEnhanced && !this.viewAsUserProfileId
					? tasks.filter((t: any) => !t.isCompleted)
					: tasks;
			},
			(err) => { console.log(err); }
		);
	}

	getOpportunities() {
		this.crmOpportunityService.getAll().subscribe(
			(res: any) => { this.opportunities = res.response?.items || []; },
			(err) => { console.log(err); }
		);
	}

	getRatingEmoji(rating: CrmInteractionRating | undefined): string {
		if (rating === CrmInteractionRating.Positive) return "😊";
		if (rating === CrmInteractionRating.Neutral) return "😐";
		if (rating === CrmInteractionRating.Negative) return "😞";
		return "—";
	}

	getRecentInteractions() {
		this.crmInteractionService.getMyRecentInteractions().subscribe(
			(res: any) => { this.recentInteractions = res.response || []; },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	getTaskCreatedByName(task: CrmTask): string {
		if (!task.createdByUserProfileId) return "";
		return this.userProfiles.find(u => u.id === task.createdByUserProfileId)?.fullName ?? "";
	}

	getTaskCustomerName(task: CrmTask): string {
		const opp = this.getTaskOpportunity(task);
		if (!opp) return "";
		if (opp.clientId) return this.clients.find(c => c.id === opp.clientId)?.name ?? "";
		if (opp.crmLeadId) return this.leads.find(l => l.id === opp.crmLeadId)?.companyName ?? "";
		return "";
	}

	getTaskDueDateLabel(dueDate: Date | undefined): string {
		if (!dueDate) return "—";
		const today = new Date(); today.setHours(0, 0, 0, 0);
		const due = new Date(dueDate); due.setHours(0, 0, 0, 0);
		const days = Math.round((due.getTime() - today.getTime()) / 86400000);
		if (days < 0) return `${Math.abs(days)}d overdue`;
		if (days === 0) return "Today";
		if (days === 1) return "Tomorrow";
		return `${days} days`;
	}

	getTaskOpportunity(task: CrmTask): CrmOpportunity | null {
		if (!task.crmOpportunityId) return null;
		return this.opportunities.find(o => o.id === task.crmOpportunityId) ?? null;
	}

	getTaskSalesValue(task: CrmTask): number | null {
		const opp = this.getTaskOpportunity(task);
		return opp ? opp.value : null;
	}

	getUpcomingEvents() {
		this.crmEventService.getUpcoming().subscribe(
			(res: any) => { this.upcomingEvents = (res.response || []).slice(0, 5); },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	getUserProfile() {
		this.authService.getCurrentUserProfile().subscribe(
			(res: any) => { this.userProfile = res.response; },
			(err) => { console.log(err); }
		);
	}

	getUserProfiles() {
		this.userProfileService.getAll().subscribe(
			(res: any) => { this.userProfiles = res.response.items; },
			(err) => { console.log(err); }
		);
	}

	goToContacts() { this.router.navigate(["admin/crmContacts"]); }
	goToEvent(id: number) { this.viewEventVisible = false; this.router.navigate([`admin/crmEvents/${id}`]); }
	goToEvents() { this.router.navigate(["admin/crmEvents"]); }
	goToInteraction(id: number) { this.viewInteractionVisible = false; this.router.navigate([`admin/crmInteractions/${id}`]); }
	goToInteractions() { this.router.navigate(["admin/crmInteractions"]); }
	goToLeads() { this.router.navigate(["admin/crmLeads"]); }
	goToTask(task: CrmTask) { if (task.crmOpportunityId) { this.router.navigate([`admin/crmOpportunities/${task.crmOpportunityId}`]); } }

	headerButtonClick(method: any) {
		// @ts-ignore
		if (this[method]) this[method]();
	}

	markFollowUpDone(followUp: CrmInteraction) {
		this.pendingDismissFollowUp = followUp;
		this.popupInteraction = new CrmInteraction();
		this.popupInteraction.interactionDate = new Date();
		this.popupInteraction.crmLeadId = followUp.crmLeadId;
		this.popupInteraction.crmContactId = followUp.crmContactId;
		this.filteredPopupContacts = followUp.crmLeadId
			? this.contacts.filter(c => c.crmLeadId === followUp.crmLeadId)
			: [];
		this.popupInteractionFollowUp = null;
		this.popupSaveLabel = "Log Interaction";
		this.popupType = "Interaction";
		this.popupVisible = true;
	}

	onViewAsChanged() {
		this.expandedTaskId = null;
		this.getMyTasks();
	}

	openContactModal() {
		this.popupContact = new CrmContact();
		this.popupSaveLabel = "Save Contact";
		this.popupType = "Contact";
		this.popupVisible = true;
	}

	openEventModal() {
		this.popupEvent = new CrmEvent();
		this.popupEvent.startDate = new Date();
		this.popupSaveLabel = "Save Event";
		this.popupType = "Event";
		this.popupVisible = true;
	}

	openInteractionModal() {
		this.popupInteraction = new CrmInteraction();
		this.popupInteraction.interactionDate = new Date();
		this.popupInteractionFollowUp = null;
		this.filteredPopupContacts = [];
		this.popupSaveLabel = "Log Interaction";
		this.popupType = "Interaction";
		this.popupVisible = true;
	}

	openLeadModal() {
		this.popupLead = new CrmLead();
		this.popupLead.isActive = true;
		this.popupSaveLabel = "Save Lead";
		this.popupType = "Lead";
		this.popupVisible = true;
	}

	openPipeline() { this.router.navigate(["admin/crmOpportunities/pipeline"]); }

	openTaskModal() {
		this.popupTask = new CrmTask();
		this.popupSaveLabel = "Save Task";
		this.popupType = "Task";
		this.popupVisible = true;
	}

	openViewEvent(event: CrmEvent) {
		this.viewEvent = event;
		this.viewEventAttendees = [];
		this.viewEventVisible = true;
		this.getEventAttendees(event.id);
	}

	openViewInteraction(interaction: CrmInteraction) {
		this.viewInteraction = interaction;
		this.viewInteractionVisible = true;
	}

	popupSave() {
		if (this.popupType === "Contact") { this.saveContact(); }
		else if (this.popupType === "Event") { this.saveEvent(); }
		else if (this.popupType === "Interaction") { this.saveInteraction(); }
		else if (this.popupType === "Lead") { this.saveLead(); }
		else if (this.popupType === "Task") { this.saveTask(); }
	}

	saveContact() {
		this.crmContactService.createSingle(this.popupContact).subscribe(
			() => { this.toastService.success("Contact saved!"); this.closePopup(); },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	saveEvent() {
		this.crmEventService.createSingle(this.popupEvent).subscribe(
			() => { this.toastService.success("Event saved!"); this.closePopup(); this.getUpcomingEvents(); },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	saveInteraction() {
		if (!this.popupInteraction.crmLeadId) { this.toastService.error("Please select a lead."); return; }
		if (!this.popupInteraction.crmContactId) { this.toastService.error("Please select a contact."); return; }
		this.popupInteraction.clientId = undefined;
		this.popupInteraction.instigatorId = this.userProfile.id;
		const outcome = this.popupInteraction.outcome || "";
		const isGoldenNugget = outcome.includes("Golden nugget");
		const isDontContact = outcome.includes("Don't contact again");
		const triggerConfetti = ["RFQ Expected", "PO Expected"].some(o => outcome.includes(o));
		this.crmInteractionService.createSingle(this.popupInteraction).subscribe(
			() => {
				this.toastService.success("Interaction logged!");
				if (isGoldenNugget) { this.showGoldRain(); } else if (isDontContact) { this.showSkull(); } else if (triggerConfetti) { this.showConfetti(); }
				if (this.pendingDismissFollowUp) { this.dismissFollowUp(this.pendingDismissFollowUp); this.pendingDismissFollowUp = null; }
				this.closePopup();
				this.getFollowUps();
				this.getRecentInteractions();
				if (this.popupInteraction.crmLeadId) {
					const lead = this.leads.find(l => l.id === this.popupInteraction.crmLeadId);
					if (lead && lead.status === CrmLeadStatus.New) {
						const updated = { ...lead, status: CrmLeadStatus.Contacted };
						this.crmLeadService.updateSingleById(lead.id, updated).subscribe(() => this.getLeads());
					}
				}
			},
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	saveLead() {
		this.crmLeadService.createSingle(this.popupLead).subscribe(
			() => { this.toastService.success("Lead saved!"); this.closePopup(); this.getLeads(); },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	saveTask() {
		if (!this.popupTask.title) { this.toastService.error("Please enter a title."); return; }
		this.crmTaskService.createSingle(this.popupTask).subscribe(
			() => { this.toastService.success("Task saved!"); this.closePopup(); this.getMyTasks(); },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	setFollowUpDays(days: number) {
		const key = `${days}d`;
		if (this.popupInteractionFollowUp === key) {
			this.popupInteractionFollowUp = null;
			this.popupInteraction.followUpDate = undefined;
		} else {
			this.popupInteractionFollowUp = key;
			const d = new Date();
			d.setDate(d.getDate() + days);
			this.popupInteraction.followUpDate = d;
		}
	}

	setFollowUpWeeks(weeks: number) {
		const key = `${weeks}w`;
		if (this.popupInteractionFollowUp === key) {
			this.popupInteractionFollowUp = null;
			this.popupInteraction.followUpDate = undefined;
		} else {
			this.popupInteractionFollowUp = key;
			const d = new Date();
			d.setDate(d.getDate() + weeks * 7);
			this.popupInteraction.followUpDate = d;
		}
	}

	showConfetti() {
		const container = document.createElement("div");
		container.style.cssText = "position:fixed;inset:0;z-index:9999;pointer-events:none;overflow:hidden";
		document.body.appendChild(container);
		const colors = ["#198754", "#0d6efd", "#ffc107", "#dc3545", "#6f42c1", "#0dcaf0", "#fd7e14"];
		for (let i = 0; i < 150; i++) {
			const piece = document.createElement("div");
			const color = colors[Math.floor(Math.random() * colors.length)];
			const left = Math.random() * 100;
			const delay = Math.random() * 2;
			const duration = 2 + Math.random() * 2;
			const size = 6 + Math.random() * 6;
			const rotation = Math.random() * 360;
			piece.style.cssText = `position:absolute;top:-20px;left:${left}%;width:${size}px;height:${size * 0.6}px;background:${color};opacity:0.9;border-radius:2px;transform:rotate(${rotation}deg);animation:crm-confetti ${duration}s ease-in ${delay}s forwards`;
			container.appendChild(piece);
		}
		if (!document.querySelector("#crm-confetti-style")) {
			const style = document.createElement("style");
			style.id = "crm-confetti-style";
			style.textContent = `@keyframes crm-confetti{0%{top:-20px;opacity:1;transform:rotate(0deg) translateX(0)}50%{opacity:1}100%{top:110vh;opacity:0;transform:rotate(720deg) translateX(${Math.random() > 0.5 ? "" : "-"}80px)}}`;
			document.head.appendChild(style);
		}
		setTimeout(() => container.remove(), 5000);
	}

	showGoldRain() {
		const overlay = document.createElement("div");
		overlay.style.cssText = "position:fixed;inset:0;z-index:9999;pointer-events:none;overflow:hidden;background:rgba(255,200,0,0.25);animation:gold-bg 4s ease-in-out forwards";
		document.body.appendChild(overlay);
		const glow = document.createElement("div");
		glow.style.cssText = "position:absolute;inset:0;background:radial-gradient(ellipse at center,rgba(255,215,0,0.3) 0%,rgba(255,180,0,0.15) 50%,transparent 80%);animation:gold-glow 3s ease-in-out forwards";
		overlay.appendChild(glow);
		for (let i = 0; i < 80; i++) {
			const coin = document.createElement("div");
			const left = Math.random() * 100;
			const delay = Math.random() * 2.5;
			const duration = 2.5 + Math.random() * 2;
			const size = 14 + Math.random() * 14;
			coin.style.cssText = `position:absolute;top:-40px;left:${left}%;width:${size}px;height:${size}px;border-radius:50%;background:radial-gradient(circle at 35% 35%,#fff8a8,#ffd700 40%,#b8860b 100%);box-shadow:0 0 6px rgba(255,215,0,0.6),inset 0 -2px 4px rgba(0,0,0,0.2);animation:gold-fall ${duration}s ease-in ${delay}s forwards`;
			overlay.appendChild(coin);
		}
		if (!document.querySelector("#gold-rain-style")) {
			const style = document.createElement("style");
			style.id = "gold-rain-style";
			style.textContent = "@keyframes gold-bg{0%{background:rgba(255,200,0,0.25)}70%{background:rgba(255,200,0,0.25)}100%{background:rgba(255,200,0,0)}}@keyframes gold-glow{0%{opacity:0}20%{opacity:1}80%{opacity:1}100%{opacity:0}}@keyframes gold-fall{0%{top:-40px;opacity:1;transform:rotateY(0deg)}50%{opacity:1}100%{top:110vh;opacity:0;transform:rotateY(1080deg)}}";
			document.head.appendChild(style);
		}
		setTimeout(() => overlay.remove(), 6000);
	}

	showSkull() {
		const overlay = document.createElement("div");
		overlay.style.cssText = "position:fixed;inset:0;z-index:9999;pointer-events:none;overflow:hidden;background:rgba(180,0,0,0.25);animation:skull-bg 2.5s ease-in-out forwards";
		document.body.appendChild(overlay);
		const skull = document.createElement("div");
		skull.innerHTML = "<i class=\"fas fa-skull-crossbones\" style=\"font-size:12rem;color:#dc3545;filter:drop-shadow(0 0 30px rgba(220,53,69,0.6))\"></i>";
		skull.style.cssText = "position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) scale(0);animation:skull-zoom 2s ease-in-out forwards";
		overlay.appendChild(skull);
		if (!document.querySelector("#skull-style")) {
			const style = document.createElement("style");
			style.id = "skull-style";
			style.textContent = "@keyframes skull-bg{0%{background:rgba(180,0,0,0.25)}60%{background:rgba(180,0,0,0.25)}100%{background:rgba(180,0,0,0)}}@keyframes skull-zoom{0%{transform:translate(-50%,-50%) scale(0);opacity:0}15%{transform:translate(-50%,-50%) scale(1.2);opacity:1}25%{transform:translate(-50%,-50%) scale(0.9);opacity:1}35%{transform:translate(-50%,-50%) scale(1.1);opacity:1}50%{transform:translate(-50%,-50%) scale(1);opacity:1}80%{transform:translate(-50%,-50%) scale(1);opacity:1}100%{transform:translate(-50%,-50%) scale(0);opacity:0}}";
			document.head.appendChild(style);
		}
		setTimeout(() => overlay.remove(), 2500);
	}

	toggleDashboardTaskComplete(task: CrmTask) {
		const updated = { ...task, completedDate: new Date(), isCompleted: true };
		this.crmTaskService.updateSingleById(task.id, updated).subscribe(
			() => { this.toastService.success("Task completed!"); this.getMyTasks(); },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	updateFilteredPopupContacts() {
		this.popupInteraction.crmContactId = undefined;
		this.filteredPopupContacts = this.popupInteraction.crmLeadId
			? this.contacts.filter(c => c.crmLeadId === this.popupInteraction.crmLeadId)
			: [];
	}
}
